function _readOnlyError(r) { throw new TypeError('"' + r + '" is read-only'); }
import { isArray } from 'vega-util';
import { isColorChannel } from '../../channel';
import { title as fieldDefTitle, valueArray } from '../../channeldef';
import { isContinuousToContinuous } from '../../scale';
import { contains, getFirstDefined } from '../../util';
import { isSignalRef } from '../../vega.schema';
import { guideFormat, guideFormatType } from '../format';
import { getFirstConditionValue } from './encode';
export var legendRules = {
  direction: function direction(_ref) {
    var _direction = _ref.direction;
    return _direction;
  },
  format: function format(_ref2) {
    var fieldOrDatumDef = _ref2.fieldOrDatumDef,
      legend = _ref2.legend,
      config = _ref2.config;
    var format = legend.format,
      formatType = legend.formatType;
    return guideFormat(fieldOrDatumDef, fieldOrDatumDef.type, format, formatType, config, false);
  },
  formatType: function formatType(_ref3) {
    var legend = _ref3.legend,
      fieldOrDatumDef = _ref3.fieldOrDatumDef,
      scaleType = _ref3.scaleType;
    var formatType = legend.formatType;
    return guideFormatType(formatType, fieldOrDatumDef, scaleType);
  },
  gradientLength: function gradientLength(params) {
    var _ref4, _legend$gradientLengt;
    var legend = params.legend,
      legendConfig = params.legendConfig;
    return (_ref4 = (_legend$gradientLengt = legend.gradientLength) !== null && _legend$gradientLengt !== void 0 ? _legend$gradientLengt : legendConfig.gradientLength) !== null && _ref4 !== void 0 ? _ref4 : defaultGradientLength(params);
  },
  labelOverlap: function labelOverlap(_ref5) {
    var _ref6, _legend$labelOverlap;
    var legend = _ref5.legend,
      legendConfig = _ref5.legendConfig,
      scaleType = _ref5.scaleType;
    return (_ref6 = (_legend$labelOverlap = legend.labelOverlap) !== null && _legend$labelOverlap !== void 0 ? _legend$labelOverlap : legendConfig.labelOverlap) !== null && _ref6 !== void 0 ? _ref6 : defaultLabelOverlap(scaleType);
  },
  symbolType: function symbolType(_ref7) {
    var _legend$symbolType;
    var legend = _ref7.legend,
      markDef = _ref7.markDef,
      channel = _ref7.channel,
      encoding = _ref7.encoding;
    return (_legend$symbolType = legend.symbolType) !== null && _legend$symbolType !== void 0 ? _legend$symbolType : defaultSymbolType(markDef.type, channel, encoding.shape, markDef.shape);
  },
  title: function title(_ref8) {
    var fieldOrDatumDef = _ref8.fieldOrDatumDef,
      config = _ref8.config;
    return fieldDefTitle(fieldOrDatumDef, config, {
      allowDisabling: true
    });
  },
  type: function type(_ref9) {
    var legendType = _ref9.legendType,
      scaleType = _ref9.scaleType,
      channel = _ref9.channel;
    if (isColorChannel(channel) && isContinuousToContinuous(scaleType)) {
      if (legendType === 'gradient') {
        return undefined;
      }
    } else if (legendType === 'symbol') {
      return undefined;
    }
    return legendType;
  },
  // depended by other property, let's define upfront
  values: function values(_ref10) {
    var fieldOrDatumDef = _ref10.fieldOrDatumDef,
      legend = _ref10.legend;
    return _values(legend, fieldOrDatumDef);
  }
};
function _values(legend, fieldOrDatumDef) {
  var vals = legend.values;
  if (isArray(vals)) {
    return valueArray(fieldOrDatumDef, vals);
  } else if (isSignalRef(vals)) {
    return vals;
  }
  return undefined;
}
export { _values as values };
export function defaultSymbolType(mark, channel, shapeChannelDef, markShape) {
  if (channel !== 'shape') {
    var _getFirstConditionVal;
    // use the value from the shape encoding or the mark config if they exist
    var shape = (_getFirstConditionVal = getFirstConditionValue(shapeChannelDef)) !== null && _getFirstConditionVal !== void 0 ? _getFirstConditionVal : markShape;
    if (shape) {
      return shape;
    }
  }
  switch (mark) {
    case 'bar':
    case 'rect':
    case 'image':
    case 'square':
      return 'square';
    case 'line':
    case 'trail':
    case 'rule':
      return 'stroke';
    case 'arc':
    case 'point':
    case 'circle':
    case 'tick':
    case 'geoshape':
    case 'area':
    case 'text':
      return 'circle';
  }
}
export function clipHeight(legendType) {
  if (legendType === 'gradient') {
    return 20;
  }
  return undefined;
}
export function getLegendType(params) {
  var legend = params.legend;
  return getFirstDefined(legend.type, defaultType(params));
}
export function defaultType(_ref11) {
  var channel = _ref11.channel,
    timeUnit = _ref11.timeUnit,
    scaleType = _ref11.scaleType;
  // Following the logic in https://github.com/vega/vega-parser/blob/master/src/parsers/legend.js
  if (isColorChannel(channel)) {
    if (contains(['quarter', 'month', 'day'], timeUnit)) {
      return 'symbol';
    }
    if (isContinuousToContinuous(scaleType)) {
      return 'gradient';
    }
  }
  return 'symbol';
}
export function getDirection(_ref12) {
  var _ref13, _legend$direction;
  var legendConfig = _ref12.legendConfig,
    legendType = _ref12.legendType,
    orient = _ref12.orient,
    legend = _ref12.legend;
  return (_ref13 = (_legend$direction = legend.direction) !== null && _legend$direction !== void 0 ? _legend$direction : legendConfig[legendType ? 'gradientDirection' : 'symbolDirection']) !== null && _ref13 !== void 0 ? _ref13 : defaultDirection(orient, legendType);
}
export function defaultDirection(orient, legendType) {
  switch (orient) {
    case 'top':
    case 'bottom':
      return 'horizontal';
    case 'left':
    case 'right':
    case 'none':
    case undefined:
      // undefined = "right" in Vega
      return undefined;
    // vertical is Vega's default
    default:
      // top-left / ...
      // For inner legend, uses compact layout like Tableau
      return legendType === 'gradient' ? 'horizontal' : undefined;
  }
}
export function defaultGradientLength(_ref14) {
  var legendConfig = _ref14.legendConfig,
    model = _ref14.model,
    direction = _ref14.direction,
    orient = _ref14.orient,
    scaleType = _ref14.scaleType;
  var gradientHorizontalMaxLength = legendConfig.gradientHorizontalMaxLength,
    gradientHorizontalMinLength = legendConfig.gradientHorizontalMinLength,
    gradientVerticalMaxLength = legendConfig.gradientVerticalMaxLength,
    gradientVerticalMinLength = legendConfig.gradientVerticalMinLength;
  if (isContinuousToContinuous(scaleType)) {
    if (direction === 'horizontal') {
      if (orient === 'top' || orient === 'bottom') {
        return gradientLengthSignal(model, 'width', gradientHorizontalMinLength, gradientHorizontalMaxLength);
      } else {
        return gradientHorizontalMinLength;
      }
    } else {
      // vertical / undefined (Vega uses vertical by default)
      return gradientLengthSignal(model, 'height', gradientVerticalMinLength, gradientVerticalMaxLength);
    }
  }
  return undefined;
}
function gradientLengthSignal(model, sizeType, min, max) {
  var sizeSignal = model.getSizeSignalRef(sizeType).signal;
  return {
    signal: "clamp(".concat(sizeSignal, ", ").concat(min, ", ").concat(max, ")")
  };
}
export function defaultLabelOverlap(scaleType) {
  if (contains(['quantile', 'threshold', 'log', 'symlog'], scaleType)) {
    return 'greedy';
  }
  return undefined;
}